/**
 * Everything that moves on the ground: one Soldier per sim entity, interpolated between the
 * last two ticks, with the pose chosen from the entity's state (actorMotion.ts). The ranger is
 * `RangerActor`'s; the kinds that are not riflemen are delegated: captives to `Captives`, the
 * automated enemies to `Machines`, tracker dogs to `Dogs` (ROUND8) and sensed mines to `MineMarkers`
 * - all driven from `update`. `handle` takes the frame's sim events for the things state alone cannot
 * say: when a throw left the hand, when and how a man began to fall, and which dog just barked.
 */

import type { Material, Scene, Texture } from 'three';
import { isDog } from '../../sim/enemies/specials/kinds';
import type { Enemy, EnemyState, SimEvent, World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { Dogs } from '../characters/dogs';
import { THROW_RELEASE_AT, THROW_SECONDS } from '../characters/poses';
import { stabBearing } from '../characters/rangerLook';
import { Soldier } from '../characters/soldier';
import { createToonMaterial, OUTLINE } from '../materials/toon';
import type { Palette } from '../palette';
import { type ActorCast, type ActorRole, lookFor, roleOf } from './actorLooks';
import { type Fall, type OneShot, poseEnemy } from './actorMotion';
import { Captives } from './Captives';
import { isMachine, Machines } from './Machines';
import { MineMarkers } from './MineMarkers';
import { discardSoldier, placeSoldier, RangerActor, twistToward } from './RangerActor';

/** A `throw` event this close to a man is his. */
const THROWER_REACH = 1.0;

/** Drawn on the soldier rig here: not a machine (`Machines`) and not a dog (`Dogs`). */
const isRifleman = (e: Enemy): boolean => !isMachine(e) && !isDog(e.kind);

export class Actors {
  private readonly ranger: RangerActor;
  private readonly enemies = new Map<number, Soldier>();
  /** Who each drawn soldier was built as (actorLooks.ts): when the sim names him as someone else, he is rebuilt. */
  private readonly roles = new Map<number, ActorRole>();
  private readonly enemyMaterial: Material;
  private readonly captives: Captives;
  private readonly machines: Machines;
  private readonly dogs: Dogs;
  private readonly mines: MineMarkers;
  /** What `handle` needs from the last `update`: events carry ids and places, not men. */
  private world: World | null = null;
  /**
   * Falls and one-shots that have been heard of but not yet drawn. They take their start time from
   * the NEXT `update`, not from the last one: frames can be far apart (a pause, a hitch, the debug
   * API stepping sixty ticks at once), and stamped with the old time the whole animation would
   * already be over by the frame that should have begun it. `lead` backdates (the throw's wind-up).
   */
  private readonly pending: Array<{ clock: { start: number }; lead: number }> = [];
  private readonly falls = new Map<number, Fall>();
  private readonly shots = new Map<number, OneShot>();
  private readonly lastState = new Map<number, EnemyState>();

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    gradient: Texture,
    private readonly groundY: (x: number, z: number) => number,
    /** Who the mission is about (`castOf(def)`). Absent - lookdev, tests - every soldier is one of the garrison. */
    private readonly cast: ActorCast | null = null,
  ) {
    // The ranger first: his is the first mesh in the scene.
    this.ranger = new RangerActor(scene, palette, gradient, groundY);
    this.enemyMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.character });
    this.captives = new Captives(scene, palette, this.enemyMaterial, groundY);
    this.machines = new Machines(scene, palette, gradient, this.enemyMaterial, groundY, cast?.timeOfDay ?? 'day');
    this.dogs = new Dogs(scene, palette, this.enemyMaterial, groundY);
    this.mines = new MineMarkers(scene, palette, gradient, groundY);
  }

  /**
   * This frame's sim events, before `update`: starts the throw, the knife thrust and the falls.
   * Optional - without it men still aim, fire, stagger and lie dead; they just never wind up or go down.
   */
  handle(events: readonly SimEvent[]): void {
    const w = this.world;
    if (!w) return;
    this.dogs.handle(events);
    let blastX = 0;
    let blastZ = 0;
    let blastR = -1;
    for (const e of events) {
      if (e.type === 'explosion') {
        // The sim pushes the blast before the casualties it causes, so the last one seen is theirs.
        blastX = e.x;
        blastZ = e.z;
        blastR = e.radius;
      } else if (e.type === 'enemyHit') {
        const man = w.enemies.find((o) => o.id === e.id);
        if (!man || !isRifleman(man)) continue;
        const was = this.lastState.get(e.id);
        const inBlast = blastR > 0 && Math.hypot(man.x - blastX, man.z - blastZ) <= blastR * 1.1;
        const blast = inBlast && e.killed;
        const away = blast ? Math.atan2(blastX - man.x, blastZ - man.z) : null;
        const fall: Fall = { start: Number.NEGATIVE_INFINITY, blast, facing: away };
        // Already on the ground there is nothing to fall from: that fall stays "long over".
        if (was !== 'down' && was !== 'wounded') this.pending.push({ clock: fall, lead: 0 });
        this.falls.set(e.id, fall);
      } else if (e.type === 'knife') {
        this.ranger.shot = { motion: 'stab', start: Number.NEGATIVE_INFINITY };
        this.pending.push({ clock: this.ranger.shot, lead: 0 });
        this.ranger.stabFacing = stabBearing(w.ranger, e);
      } else if (e.type === 'throw') {
        // The event marks the release; the wind-up was the stance he stood in with it in his hand.
        const shot: OneShot = { motion: 'throw', start: Number.NEGATIVE_INFINITY };
        this.pending.push({ clock: shot, lead: THROW_RELEASE_AT * THROW_SECONDS });
        const r = w.ranger;
        if (Math.hypot(r.x - e.x, r.z - e.z) <= THROWER_REACH) this.ranger.shot = shot;
        else {
          const thrower = w.enemies.find((o) => isRifleman(o) && Math.hypot(o.x - e.x, o.z - e.z) <= THROWER_REACH);
          if (thrower) this.shots.set(thrower.id, shot);
        }
      }
    }
  }

  update(world: World, time: number, alpha: number, camera: PixelCamera): void {
    this.world = world;
    for (const p of this.pending) p.clock.start = time - p.lead;
    this.pending.length = 0;
    this.ranger.update(world, time, alpha, camera);

    const present = new Set<number>();
    for (const e of world.enemies) {
      if (!isRifleman(e)) continue;
      present.add(e.id);
      this.lastState.set(e.id, e.state);
      let soldier = this.enemies.get(e.id);
      const role = roleOf(e, world, this.cast);
      if (soldier && this.roles.get(e.id) !== role) {
        // The sim has just said who he is (the ids are -1 until the first ground tick): dress him for it, once.
        discardSoldier(this.scene, soldier);
        soldier = undefined;
      }
      if (!soldier) {
        // Reinforcements arrive mid-mission: build their model the first time they are seen.
        soldier = new Soldier(this.palette, this.enemyMaterial, lookFor(e, role, this.cast));
        this.enemies.set(e.id, soldier);
        this.roles.set(e.id, role);
        this.scene.add(soldier.mesh);
      }
      soldier.mesh.visible = true;
      placeSoldier(soldier, this.groundY, e.prevX, e.prevZ, e.x, e.z, alpha, 0);
      const s = soldier.state;
      soldier.clearOverlays();
      const fall = this.falls.get(e.id) ?? null;
      // Thrown by a blast he lands on his back pointing away from it, whichever way he had been looking.
      s.facing = e.state === 'dead' && fall && fall.facing !== null ? fall.facing : e.facing;
      s.travelled = e.travelled;
      // A man on the move who can see the ranger keeps his rifle on him.
      const covering = e.state === 'combat' && e.seesRanger;
      const bearing = Math.atan2(world.ranger.x - e.x, world.ranger.z - e.z);
      s.twist = covering ? twistToward(e.facing, bearing) : 0;
      poseEnemy(s, e, world.alert, time, this.shots.get(e.id) ?? null, fall);
      soldier.update(time, camera);
    }
    // Men the sim has taken away - the officer when he surrenders (he comes back as a captive), one who
    // escaped off the map - stop being drawn.
    for (const [id, soldier] of this.enemies) {
      if (present.has(id)) continue;
      soldier.mesh.visible = false;
      this.falls.delete(id);
      this.shots.delete(id);
      this.lastState.delete(id);
    }

    this.captives.update(world, time, alpha, camera);
    this.machines.update(world, time, alpha, camera);
    this.dogs.update(world, time, alpha, camera);
    this.mines.update(world);
  }

  /** Remove everything this class put in the scene and free its GPU resources. Call when the mission ends. */
  dispose(): void {
    this.ranger.dispose();
    for (const soldier of this.enemies.values()) discardSoldier(this.scene, soldier);
    this.enemies.clear();
    this.roles.clear();
    this.falls.clear();
    this.shots.clear();
    this.lastState.clear();
    this.pending.length = 0;
    this.world = null;
    this.captives.dispose();
    this.machines.dispose();
    this.dogs.dispose();
    this.mines.dispose();
    this.enemyMaterial.dispose();
  }
}
